/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { empty, errorResponse, isArray, isObject, prepareResponseData, toAmountFormat } from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import studentApi from "../../api/Student";
import feeApi from "../../api/Fees";
import classesApi from "../../api/Classes";
import paymentApi from "../../api/Payment";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import { AuthContext } from "../Root/ProtectedRoute";
import SelectField from "../../components/form/SelectField";
import { Editor } from "primereact/editor";
import Header4 from "../../components/headers/Header4";
import colors from "../../config/colors";
import { paymentMethod } from "../../data/PaymentStatus";
import PaymentWrapper from "../../components/appWrapper/PaymentWrapper";
import AppCheckbox from "../../components/form/AppCheckbox";
import { FaCheckCircle } from "react-icons/fa";

const required = "This field is required!";
const initialValues = {
  class_id: '',
  student_id: '',
  date: '',
  method: '',
  amount: ''
};

const AddUpdatePayment = ({ ...props }) => {
  const { user, setStudentData } = useContext(AuthContext);
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const toastTR = useRef(null);

  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [remark, setRemark] = useState('');
  const [classes, setClasses] = useState([]);
  const [feePayableDetails, setFeePayableDetails] = useState({});
  const [debtsCleared, setDebtsCleared] = useState(false);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    getClasses();
  }, []);

  useEffect(() => {
    
  }, [feePayableDetails])

const validationSchema = Yup.object().shape({
  class_id: Yup.string().required(required),
  student_id: Yup.string().required(required),
  date: Yup.string().required(required),
  method: Yup.string().required(required),
  amount: Yup.number().required(required)
});

  /**
   * Get Classes
   * @returns 
   */
  const getClasses = async () => {
    try {
      const response = await classesApi.getClasses(null);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.success) {
        return setClasses([]);
      } else {
        setClasses(isArray(response_data?.response) ? response_data?.response : []);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  /**
   * Get All Stuents
   * @returns 
   */
  const getStudentsInClass = async (classId) => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await studentApi.getStudentsInClass(schoolId, null, null, null, classId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.success) {
        return setStudents([]);
      } else {
        setStudents(response_data?.response || []);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get fee details
   * @returns 
   */
  const getFeesPayable = async (studentId) => {
    if (!isLoading) setIsLoading(true);
    try {
      const response = await feeApi.getFeesPayable(studentId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to get fee details."
        );
      }

      const data = isObject(response_data?.response) ? response_data.response : {};
      if (data?.hasPendingFee) {
        if (!data?.invoiceId) {
          return responseDialog("error", "Error Alert", "Something went wrong. Please refresh the page or try again later.");
        }
        return navigate(`/finance/invoice/${encodeURIComponent(data.invoiceId)}`);
      }
      if (data?.feesCleared) {
        setDebtsCleared(data?.feesCleared);
        return responseDialog("success", "Debts Cleared", "This student has no outstanding fees/debts");
      }
      setFeePayableDetails(data);
      setStudentData({feesPayable: data, _id: studentId });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle submission
   * @param values
   */
  const handleSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Validation Error!",
          "Something went wrong! Try again later."
        );
      }

      let response;
      const fees = isObject(feePayableDetails) ? feePayableDetails : {}
      const payload = {
        fees,
        student_id: values?.student_id || '',
        date: values?.date || '',
        method: values?.method || '',
        amount: values?.amount || 0,
        remark
      }

      // valide fees 
      const feesOwed = isArray(fees?.feesOwed) ? fees.feesOwed : [];
      const installments = isArray(fees?.installments) ? fees.installments : [];
      let isAFeeSelected = false;
      feesOwed.forEach((feeOwed) => {
        const fees = isArray(feeOwed?.fees) ? feeOwed.fees : [];
        fees.forEach((fee) => {
          if (fee?.isSelected) {
            isAFeeSelected = true;
            return;
          }
        })
      });
      installments.forEach((feeOwed) => {
        const fees = isArray(feeOwed?.feeItems) ? feeOwed.feeItems : [];
        fees.forEach((fee) => {
          if (fee?.isSelected) {
            isAFeeSelected = true;
            return;
          }
        })
      });

      if (!isAFeeSelected) {
        return responseDialog(
          "error",
          "Error Alert",
          "Please select a fee."
        );
      }

      if (!invoiceId) {
        response = await paymentApi.addPayment(payload);
      } else {
        payload.fee_id = invoiceId;
        response = await paymentApi.updatePayment(
          payload
        );
      }
      const response_data = prepareResponseData(response);
      if (!response_data?.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Something went wrong!"
        );
      }

      const _invoiceId = response_data?.response?.invoiceId || '';
      if (!empty(_invoiceId)) {
        return navigate(`/finance/invoice/${encodeURIComponent(_invoiceId)}`);
      }
      responseDialog("error", "Error Alert", "Something went wrong while processing your request.");
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle input type for fee category and amount
   */
  const handleInputType = (name, setFieldValue, index) => async (e) => {
    const value = e.target?.value || "";

    // Update Formik values
    setFieldValue(name, value);

    if (name === "class_id") {
      await getStudentsInClass(value);
    }

    if (name === "student_id") {
      await getFeesPayable(value);
    }
  };

  return (
    <>
      <PaymentWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader
              title={
                !empty(invoiceId)
                  ? "Update Payment"
                  : "Add Payment"
              }
            />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
              {({ values, setFieldValue }) => (
                <Form style={{ width: "100%" }}>
                  <div className="app_input_group">
                    <SelectField
                      labelTitle="Class"
                      placeholder="Select Class"
                      name="class_id"
                      options={classes || []}
                      height={50}
                      required={true}
                      valueKey="_id"
                      optionLabel='title'
                      selectedOption={values.class_id}
                      handleChangeFunc={handleInputType(
                        "class_id",
                        setFieldValue
                      )}
                    />
                  </div>
                    
                  { values?.class_id && 
                    <div className="app_input_group mt-10">
                      <SelectField
                        labelTitle="Student"
                        required={true}
                        placeholder="Select Student"
                        name="student_id"
                        options={students}
                        height={50}
                        valueKey="_id"
                        display="fullName"
                        selectedOption={values.student_id}
                        handleChangeFunc={handleInputType(
                          "student_id",
                          setFieldValue
                        )}
                      />
                      </div>
                    }
                    
                    { !empty(feePayableDetails?.feesOwed) || !empty(feePayableDetails?.installments) ?
                      <>
                        <Header4>Fees Breakdown <span className="text-red">*</span>: </Header4>
                        <div className="mb-10"><strong>Total Fees Owed: </strong>{ user?.currency_symbol || ""}{ toAmountFormat((feePayableDetails?.totalFeeOwed || 0) + (feePayableDetails?.installmentOwed || 0))}</div>
                        <div className="bold fs-14 mb-10">Categories</div>
                        {!empty(feePayableDetails?.feesOwed) &&
                          <>
                            <div className="mt-20"><strong>- Fees Owed:</strong> { user?.currency_symbol || ""}{ toAmountFormat(feePayableDetails?.totalFeeOwed) || "0.0" }</div>
                            <div className="app_input_group">
                            {Array.isArray(feePayableDetails?.feesOwed) && (
                              <div className="flex direction-column mb-20" style={{ width: "100%" }}>
                                <div style={{ marginTop: -10 }}>
                                  {feePayableDetails.feesOwed.map((feeOwed, index) => {
                                    const fees = Array.isArray(feeOwed?.fees) ? feeOwed.fees : [];

                                    const handleCheckboxChange = (feeIndex) => {
                                      const updatedFeePayableDetails = { ...feePayableDetails };
                                      updatedFeePayableDetails.feesOwed = [...feePayableDetails.feesOwed];
                                      const updatedFees = [...fees];
                                      updatedFees[feeIndex] = {
                                        ...updatedFees[feeIndex],
                                        isSelected: !updatedFees[feeIndex]?.isSelected,
                                      };
                                      updatedFeePayableDetails.feesOwed[index] = {
                                        ...feeOwed,
                                        fees: updatedFees,
                                      };
                                      setFeePayableDetails(updatedFeePayableDetails);
                                    };

                                    return (
                                      <div key={feeOwed._id || index}>
                                        <div
                                          style={{
                                            backgroundColor: colors.mainSecondary,
                                            padding: "5px 10px",
                                            width: "100%",
                                            marginTop: 20
                                          }}
                                        >
                                          <span className="bold text-white">
                                            {feeOwed?.title} -&gt;
                                          </span>
                                        </div>

                                        <div className="mt-10 mb-20">
                                          <strong>Description:</strong>
                                          <section
                                            style={{ marginTop: -8, color: colors.gray, fontSize: 14 }}
                                            dangerouslySetInnerHTML={{ __html: feeOwed?.description }}
                                          />
                                        </div>

                                        <div>
                                          {fees.map((fee, idx) => (
                                            <div
                                              key={fee?._id || idx}
                                              style={{
                                                paddingLeft: 10,
                                                marginBottom: 5,
                                                borderBottom: `1px solid ${colors.lightPrimary}`,
                                                paddingTop: 12,
                                                backgroundColor: colors.lightPrimary,
                                              }}
                                            >
                                              <div className="flex direction-row flex-start">
                                                <AppCheckbox
                                                  index={idx}
                                                  onChange={() => handleCheckboxChange(idx)}
                                                  checked={fee?.isSelected}
                                                  value={fee?._id}
                                                />
                                                <span>
                                                  <strong>{fee?.categoryTitle || ""}:</strong> { user?.currency_symbol || ""}{toAmountFormat(fee?.amount) || "0.0"}
                                                </span>
                                              </div>
                                              <div className="mt-10 fs-14">({fee?.sessionTitle || ''} - {fee?.termTitle || ''})</div>
                                              <section
                                                style={{ marginTop: -8, color: colors.gray, fontSize: 14 }}
                                                dangerouslySetInnerHTML={{
                                                  __html: fee?.categoryDescription || "",
                                                }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            </div>
                          </>
                        }

                        { !empty(feePayableDetails?.installments) &&
                          <>
                            <div className="mt-10"><strong>- Installments:</strong> Paid: { user?.currency_symbol || ""}{ toAmountFormat(feePayableDetails?.totalInstallmentsPaid) || "0.0" } - To Pay: { user?.currency_symbol || ""}{ toAmountFormat(feePayableDetails?.installmentOwed) || "0.0" }</div>
                            <div className="app_input_group">
                              {Array.isArray(feePayableDetails?.installments) && (
                                <div className="flex direction-column mb-20" style={{ width: "100%" }}>
                                  <div style={{ marginTop: -10 }}>
                                    {feePayableDetails.installments.map((installment, index) => {
                                      const fees = Array.isArray(installment?.feeItems) ? installment.feeItems : [];

                                      const handleCheckboxChange = (feeIndex) => {
                                        const updatedFeePayableDetails = { ...feePayableDetails };
                                        updatedFeePayableDetails.installments = [...feePayableDetails.installments];
                                        const updatedFees = [...fees];
                                        updatedFees[feeIndex] = {
                                          ...updatedFees[feeIndex],
                                          isSelected: !updatedFees[feeIndex]?.isSelected,
                                        };
                                        updatedFeePayableDetails.installments[index] = {
                                          ...installment,
                                          feeItems: updatedFees,
                                        };
                                        setFeePayableDetails(updatedFeePayableDetails);
                                      };

                                      return (
                                        <div key={installment._id || index}>
                                          <div
                                            style={{
                                              backgroundColor: colors.mainSecondary,
                                              padding: "5px 10px",
                                              width: "100%",
                                              marginTop: 20
                                            }}
                                          >
                                            <span className="bold text-white">
                                              {installment?.title} -&gt;
                                            </span>
                                          </div>

                                          <div className="mt-10 mb-20">
                                            <strong>Description:</strong>
                                            <section
                                              style={{ marginTop: -8, color: colors.gray, fontSize: 14 }}
                                              dangerouslySetInnerHTML={{ __html: installment?.description }}
                                            />
                                          </div>

                                          <div>
                                            {fees.map((fee, idx) => (
                                              <div
                                                key={fee?._id || idx}
                                                style={{
                                                  paddingLeft: 10,
                                                  marginBottom: 5,
                                                  borderBottom: `1px solid ${colors.lightPrimary}`,
                                                  paddingTop: 12,
                                                  backgroundColor: colors.lightPrimary,
                                                }}
                                              >
                                                <div className="flex direction-row flex-start">
                                                  <AppCheckbox
                                                    index={idx}
                                                    onChange={() => handleCheckboxChange(idx)}
                                                    checked={fee?.paidInFull ? true : fee?.isSelected ? true : false}
                                                    value={fee?._id}
                                                    {...(fee?.paidInFull ? { disabled: true } : {})}
                                                  />
                                                  <span>
                                                    <strong>{fee?.categoryTitle || ""}:</strong> {user?.currency_symbol || ""}{toAmountFormat(fee?.amount || 0)} - (<em>Paid: {user?.currency_symbol || ""}{toAmountFormat(fee?.amountPaid || 0)} {fee?.paidInFull ? " - Paid in full " : ` - Bal: ${user?.currency_symbol || ""}${toAmountFormat(fee?.amount - fee?.amountPaid)}`}</em>)
                                                  </span>
                                                </div>
                                                <div className="mt-10 fs-14">({fee?.sessionTitle || ''} - {fee?.termTitle || ''})</div>
                                                <section
                                                  style={{ marginTop: -8, color: colors.gray, fontSize: 14 }}
                                                  dangerouslySetInnerHTML={{
                                                    __html: fee?.categoryDescription || "",
                                                  }}
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div> 
                          </>
                        }
                    
                        <div className="app_input_group">
                          <InputField
                            placeholder="Enter Amount"
                            name="amount"
                            height={50}
                            type='number'
                            containerWidth={300}
                            labelTitle="Amount"
                            required={true}
                          />
                        </div>
                          
                        <div className="app_input_group">
                          <SelectField
                            labelTitle="Payment Method"
                            required={true}
                            placeholder="Select Payment Method"
                            name="method"
                            options={paymentMethod}
                            height={50}
                            valueKey="title"
                            display="title"
                            containerWidth={300}
                            selectedOption={values.method}
                            handleChangeFunc={handleInputType(
                              "method",
                              setFieldValue
                            )}
                          />
                        </div>
                        
                        <div className="app_input_group">
                          <InputField
                            placeholder="Enter Payment Date"
                            name="date"
                            height={50}
                            type='date'
                            containerWidth={300}
                            labelTitle="Payment Date"
                            required={true}
                          />
                        </div>
                        <Header4>
                          <span className="fs-16">
                            Remarks (Optional)
                          </span>
                        </Header4>
                        <div style={{ width: '100%' }}>
                          <Editor value={remark} onTextChange={(e) => setRemark(e.htmlValue)} style={{ height: '320px', backgroundColor: colors.white }} />
                        </div>
                        
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <ButtonIcon
                            height={45}
                            marginTop={5}
                            color="#ffffff"
                            backgroundColor="#633ccd"
                            width={300}
                            borderColor="#633ccd"
                            buttonText={
                              invoiceId ? "Update Payment" : "Add Payment"
                            }
                            type="submit"
                          />
                        </div>
                        </> : <div></div>
                      }
                </Form>
              )}
              </Formik>
            </div>
            <div style={{ width: "100%" }} className="flex justify-center ">
              {
                debtsCleared &&
                  <div className="mt-30 flex align-center justify-center direction-column">
                    <div>
                      <FaCheckCircle color={colors?.success} size={200} />
                    </div>
                    <div className="ff-sansita mt-20 fs-20" style={{ width: "100%"}}>This student has no outstanding fees/debts</div>
                  </div>
              }
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </PaymentWrapper>
    </>
  );
};

export default AddUpdatePayment;
